import React, { useContext, useState } from 'react';
import {
  SelectField,
  InputField,
  CheckboxField
} from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import TrusteeAdditionalPhoneNumberFields from './TrusteeAdditionalPhoneNumberFields';
import {
  parsePhoneForDisplay,
  states
} from '../ContactInformationScreen/utils';
import {
  addressLine1Validation,
  poBoxLine1Validation,
  addressLine2Validation,
  poBoxLine2Validation,
  valueRequired,
  phoneValidation,
  emailValidation,
  zipCodeValidation,
  cityValidation,
  stateValidation,
  mailingCityValidation,
  mailingStateValidation
} from '../../flows/shared/validations';
import { handleInputFormat } from '../../flows/shared/utils';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import styles from './styles.css';
import Heading from '../../components/Typography/Heading/Heading';
import { getUserInfo, withDefault } from '../../core/util';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

const TrusteeContactInformationScreen = () => {
  const { back, next, formProps, appState } = useContext(FlowContext);
  const [mailingAddressDiffValue, setMailingAddressDiffValue] = useState(
    appState.store.mailingAddressDifferent
  );

  const {
    store: { app, accountType, prospect }
  } = appState;
  const { trustee = {}, customer = {} } = app;

  const toggleMailingAddressDifferent = () =>
    setMailingAddressDiffValue(!mailingAddressDiffValue);

  const {
    residentialAddress,
    mailingAddress,
    phones,
    primaryEmailAddress
  } = getUserInfo({
    accountType,
    trustee,
    customer
  });
  const mobilePhone = parsePhoneForDisplay(phones, 'MOBILE');
  const [phoneValue, setPhoneValue] = useState(withDefault(mobilePhone, ''));

  return (
    <div className="contact-information">
      <div className="title-wrapper">
        <Heading text="Enter your remaining contact information" />
      </div>

      <div className="address-fields">
        <div className="address-line1">
          <InputField
            title="Residential / Legal Address Line 1"
            {...formProps.residentialStreet1}
            defaultValue={residentialAddress?.street1}
            validation={[
              addressLine1Validation,
              poBoxLine1Validation,
              valueRequired
            ]}
            maxLength="35"
            className="blocked_data"
            data-name={generateGeneralTestID(
              'TrusteeContactInformation',
              'TrusteeResidentialStreetOne'
            )}
          />
        </div>

        <div className="address-line2">
          <InputField
            title="Residential / Legal Address Line 2"
            {...formProps.residentialStreet2}
            defaultValue={residentialAddress?.street2}
            validation={[addressLine2Validation, poBoxLine2Validation]}
            maxLength="35"
            className="blocked_data"
            data-name={generateGeneralTestID(
              'TrusteeContactInformation',
              'TrusteeResidentialStreetTwo'
            )}
          />
        </div>
      </div>
      <div className="address-fields">
        <div className="city-input">
          <InputField
            title="City"
            {...formProps.residentialCity}
            defaultValue={residentialAddress?.city}
            validation={[cityValidation, valueRequired]}
            className="blocked_data"
            data-name={generateGeneralTestID(
              'TrusteeContactInformation',
              'TrusteeResidentialCity'
            )}
          />
        </div>
        <div className="zipcode-input">
          <InputField
            title="Zip Code"
            {...formProps.residentialZip}
            defaultValue={residentialAddress?.zip}
            validation={[zipCodeValidation, valueRequired]}
            maxLength="5"
            className="blocked_data"
            data-name={generateGeneralTestID(
              'TrusteeContactInformation',
              'TrusteeResidentialZip'
            )}
          />
        </div>
        <div className="state-input">
          <SelectField
            title="State"
            {...formProps.residentialState}
            defaultValue={residentialAddress?.state}
            validation={[stateValidation, valueRequired]}
            data-name={generateGeneralTestID(
              'TrusteeContactInformation',
              'TrusteeState'
            )}>
            <option hidden value="">
              State
            </option>
            {states.map(abrr => {
              return (
                <option
                  key={abrr}
                  value={abrr}
                  data-name={generateGeneralTestID(
                    'TrusteeContactInformation',
                    `SelectResidentialState_${abrr}`
                  )}>
                  {abrr}
                </option>
              );
            })}
          </SelectField>
        </div>
      </div>

      <CheckboxField
        name="mailingAddressDifferent"
        title="My mailing address is different from my residential/legal address"
        onClick={toggleMailingAddressDifferent}
        checked={mailingAddressDiffValue}
        data-name={generateGeneralTestID(
          'TrusteeContactInformation',
          'DifferentMailingAddressCheckbox'
        )}
        {...formProps.mailingAddressDifferent}
      />

      {mailingAddressDiffValue && (
        <div>
          <div className="address-fields">
            <div className="address-line1">
              <InputField
                title="Mailing Address"
                {...formProps.mailingStreet1}
                defaultValue={mailingAddress?.street1}
                validation={addressLine1Validation}
                maxLength="35"
                className="blocked_data"
                data-name={generateGeneralTestID(
                  'TrusteeContactInformation',
                  'TrusteeMailingStreetOne'
                )}
              />
            </div>
            <div className="address-line2">
              <InputField
                title="Address Line 2"
                {...formProps.mailingStreet2}
                defaultValue={mailingAddress?.street2}
                validation={addressLine2Validation}
                maxLength="35"
                className="blocked_data"
                data-name={generateGeneralTestID(
                  'TrusteeContactInformation',
                  'TrusteeMailingStreetTwo'
                )}
              />
            </div>
          </div>
          <div className="address-fields">
            <div className="city-input">
              <InputField
                title="City"
                {...formProps.mailingCity}
                defaultValue={mailingAddress?.city}
                validation={mailingCityValidation}
                className="blocked_data"
                data-name={generateGeneralTestID(
                  'TrusteeContactInformation',
                  'TrusteeMailingCity'
                )}
              />
            </div>
            <div className="zipcode-input">
              <InputField
                title="Zip Code"
                {...formProps.mailingZip}
                defaultValue={mailingAddress?.zip}
                validation={zipCodeValidation}
                maxLength="5"
                className="blocked_data"
                data-name={generateGeneralTestID(
                  'TrusteeContactInformation',
                  'TrusteeMailingZip'
                )}
              />
            </div>
            <div className="state-input">
              <SelectField
                title="State"
                {...formProps.mailingState}
                defaultValue={mailingAddress?.state}
                validation={mailingStateValidation}
                data-name={generateGeneralTestID(
                  'TrusteeContactInformation',
                  'TrusteeMailingState'
                )}>
                <option hidden value="">
                  State
                </option>
                {states.map(abrr => {
                  return (
                    <option
                      key={abrr}
                      value={abrr}
                      data-name={generateGeneralTestID(
                        'TrusteeContactInformation',
                        `SelectMailingState_${abrr}`
                      )}>
                      {abrr}
                    </option>
                  );
                })}
              </SelectField>
            </div>
          </div>
        </div>
      )}
      <div className="contact-fields">
        <div className="phone-input">
          <InputField
            title="Mobile phone"
            placeholder="(000) 000-0000"
            onChange={evt => handleInputFormat(evt, 'PHONE', setPhoneValue)}
            maxLength="14"
            autocomplete="new-password"
            validation={phoneValidation}
            {...formProps.phone1}
            value={phoneValue}
            className="blocked_data"
            data-name={generateGeneralTestID(
              'TrusteeContactInformation',
              'TrusteeMobilePhoneNumber'
            )}
          />
        </div>

        <div className="email-input">
          <InputField
            title="Email"
            {...formProps.primaryEmailAddress}
            defaultValue={primaryEmailAddress}
            disabled={
              prospect
            } /* disable email fields for prospect due to email verification */
            validation={[emailValidation, valueRequired]}
            className="blocked_data"
            data-name={generateGeneralTestID(
              'TrusteeContactInformation',
              'TrusteeEmail'
            )}
          />
        </div>
      </div>

      <TrusteeAdditionalPhoneNumberFields
        formProps={formProps}
        appState={appState}
      />
      <div className="button-container">
        <MFBeaconButton
          name="next-button"
          variant="primary"
          size="normal"
          id="trusteeContactInformationContinue"
          data-name={generateGeneralTestID(
            'TrusteeContactInformation',
            'ContinueButton'
          )}
          className="continue-btn"
          onClick={next({
            section: 'addressValidation',
            mailingAddressDiffValue
          })}
          label="Continue"
        />
        <MFBeaconButton
          variant="secondary"
          size="normal"
          id="trusteeContactInformationBack"
          onClick={back()}
          label="Back"
          data-name={generateGeneralTestID(
            'TrusteeContactInformation',
            'BackButton'
          )}
        />
      </div>
      <PhoneNumber />
      <style jsx>{styles}</style>
    </div>
  );
};

export default TrusteeContactInformationScreen;
